import { Component, DestroyRef, inject, signal, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule, NgForm, NgModel } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import { ThemedIconComponent } from '../../atoms/themed-icon/themed-icon.component';
import { CreateFormStrategy } from './create-form.interface';
import { Observable } from 'rxjs';
import { HlmButtonImports } from '@spartan-ng/helm/button';
import { HlmInput } from '@spartan-ng/helm/input';
import { BrnDialogRef, injectBrnDialogContext } from '@spartan-ng/brain/dialog';

interface CreateFormErrorMessages {
  nameConflict: string;
  generic: string;
}

@Component({
  selector: 'ocp-create-form',
  templateUrl: './create-form.component.html',
  styleUrls: ['./create-form.component.scss'],
  imports: [HlmButtonImports, HlmInput, FormsModule, TranslatePipe, ThemedIconComponent],
})
export class CreateFormComponent {
  private readonly destroyRef = inject(DestroyRef);
  private readonly form = viewChild.required<NgForm>('createForm');
  private readonly nameInputRef = viewChild<NgModel>('nameInput');
  protected readonly dialogRef = inject<BrnDialogRef<unknown>>(BrnDialogRef, { optional: true });
  private readonly _dialogContext = injectBrnDialogContext<{
    strategy: CreateFormStrategy;
    onSubmit?: (name: string) => Observable<unknown>;
    errorMessages?: CreateFormErrorMessages;
    initialValue?: string;
  }>({ optional: true });

  protected readonly strategy =
    this._dialogContext?.strategy ?? inject(CreateFormStrategy, { optional: true });
  protected readonly minLength = 3;

  private readonly onSubmitFn: ((name: string) => Observable<unknown>) | undefined =
    this._dialogContext?.onSubmit;
  private readonly errorMessages: CreateFormErrorMessages | undefined =
    this._dialogContext?.errorMessages;

  readonly name = signal(this._dialogContext?.initialValue ?? '');
  protected readonly isSubmitting = signal(false);

  protected onSubmit(): void {
    if (!this.validateForm()) {
      return;
    }

    const name = this.name().trim();

    if (this.onSubmitFn) {
      this.isSubmitting.set(true);
      this.onSubmitFn(name)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: result => {
            this.isSubmitting.set(false);
            this.dialogRef?.close(result);
          },
          error: err => {
            this.isSubmitting.set(false);
            if (this.errorMessages) {
              const status = err?.status;
              const shouldUseBackendMessage = status === 400 || status === 403 || status === 404;
              let backendMessage: string | undefined;
              if (typeof err?.responseText === 'string') {
                backendMessage = err.responseText;
              } else if (typeof err?.error === 'string') {
                backendMessage = err.error;
              } else if (typeof err?.error?.message === 'string') {
                backendMessage = err.error.message;
              }

              let serverError = this.errorMessages.generic;
              if (shouldUseBackendMessage) {
                if (backendMessage) {
                  const statusLabelMap: Record<number, string> = {
                    400: 'Bad Request',
                    403: 'Forbidden',
                    404: 'Not Found',
                  };
                  const statusLabel = statusLabelMap[status] ?? 'Unknown';
                  serverError = `Error ${status} (${statusLabel}): ${backendMessage.trim()}`;
                } else {
                  serverError = this.errorMessages.generic;
                }
              } else if (status === 409) {
                serverError = this.errorMessages.nameConflict;
              }

              this.nameInputRef()?.control.setErrors({
                serverError,
              });
            }
          },
        });
    } else {
      this.dialogRef?.close({ name });
    }
  }

  protected onNameChange(value: string): void {
    this.name.set(value);

    const control = this.nameInputRef()?.control;
    const errors = control?.errors;
    if (!control || !errors || !('serverError' in errors)) {
      return;
    }

    const rest = Object.fromEntries(
      Object.entries(errors).filter(([key]) => key !== 'serverError')
    );
    control.setErrors(Object.keys(rest).length > 0 ? rest : null);
  }

  private validateForm(): boolean {
    const form = this.form();
    form.form.markAllAsTouched();

    return !form.invalid;
  }
}
